import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Plus, Pencil, Trash2, Star, StarOff } from "lucide-react";
import {
  fetchLanguages,
  deleteLanguage,
  clearError,
} from "../../redux/slices/languageSlice";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { useToast } from "../../components/ui/use-toast";
import LanguageDialog from "./LanguageDialog";
import api from "../../services/api";

const Languages = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { languages = [], loading } = useSelector((state) => state.language);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [languageToDelete, setLanguageToDelete] = useState(null);

  const languagesFetched = React.useRef(false);

  useEffect(() => {
    if (!languagesFetched.current) {
      languagesFetched.current = true;
      dispatch(fetchLanguages());
    }
  }, [dispatch]);

  const handleEdit = (language) => {
    setEditingLanguage(language);
  };

  const handleDeleteClick = (language) => {
    if (language.is_default) {
      toast({
        title: "Error",
        description: "Cannot delete the default language",
        variant: "destructive",
      });
      return;
    }
    setLanguageToDelete(language);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteLanguage(languageToDelete.id)).unwrap();
      toast({
        title: "Success",
        description: "Language deleted successfully",
      });
      setDeleteDialogOpen(false);
      setLanguageToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: error || "Failed to delete language",
        variant: "destructive",
      });
    }
  };

  const handleSetDefault = async (languageId) => {
    try {
      await api.post(`/admin/languages/${languageId}/set-default`);
      toast({
        title: "Success",
        description: "Default language updated successfully",
      });
      dispatch(fetchLanguages());
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to set default language",
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (language) => {
    if (language.is_default && language.is_active) {
      toast({
        title: "Error",
        description: "Cannot deactivate the default language",
        variant: "destructive",
      });
      return;
    }

    try {
      await api.put(`/admin/languages/${language.id}`, {
        ...language,
        is_active: !language.is_active,
      });
      toast({
        title: "Success",
        description: `Language ${
          language.is_active ? "deactivated" : "activated"
        } successfully`,
      });
      dispatch(fetchLanguages());
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to update language",
        variant: "destructive",
      });
    }
  };

  const sortedLanguages = [...languages].sort(
    (a, b) => a.sort_order - b.sort_order
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Language Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage supported languages for the application
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Language
        </Button>
      </div>

      {/* Languages Table */}
      <Card>
        <CardHeader>
          <CardTitle>Configured Languages</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : sortedLanguages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No languages configured</p>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add First Language
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Native Name</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedLanguages.map((language) => (
                  <TableRow key={language.id}>
                    <TableCell className="font-medium">
                      <Badge variant="outline">{language.code}</Badge>
                    </TableCell>
                    <TableCell>{language.name}</TableCell>
                    <TableCell
                      className={
                        language.direction === "rtl" ? "text-right" : ""
                      }
                    >
                      {language.native_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {language.direction.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={language.is_active}
                          onCheckedChange={() => handleToggleActive(language)}
                          disabled={language.is_default && language.is_active}
                        />
                        <span className="text-sm">
                          {language.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {language.is_default ? (
                        <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                          <Star className="h-3 w-3 mr-1 fill-current" />
                          Default
                        </Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefault(language.id)}
                        >
                          <StarOff className="h-3 w-3 mr-1" />
                          Set Default
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>{language.sort_order}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(language)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteClick(language)}
                          disabled={language.is_default}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <LanguageDialog
        open={isCreateDialogOpen || !!editingLanguage}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setEditingLanguage(null);
        }}
        language={editingLanguage}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Language</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{languageToDelete?.name}"? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Languages;
